import type { FC } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import AIReadyDataPng2x from './assets/AIReadyData@2x.png'
import AIReadyDataWebp from './assets/AIReadyData.webp'
import AIReadyDataWebp2x from './assets/AIReadyData@2x.webp'

const copy = {
  overline: `AI-ready data`,
  heading: `Deepen your understanding of each customer with contextualized customer data`,
  list: [
    {
      heading: `Linked Profiles`,
      description: `Enables data teams to map a Data Graph that brings together the power of the Segment real-time customer data platform with the complete set of business records stored in data warehouses and lakes—promoting a deeper understanding of each customer and their relationship and interactions with your business.`,
    },
    {
      heading: `Zero Copy Architecture`,
      description: `Query and access sensitive source data in the warehouse or data lake where it lives, without needing to create complex ETL jobs, for easier activation of AI models in downstream applications.`,
    },
  ],
  buttonText: `Explore Unify`,
  buttonUrl: `/product/unify/`,
}

const ReadyData: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <SectionBase variant="2Col" reverse={true}>
      <SectionCopy>
        <CopyTextBase>
          <CopyText.Overline>{copy.overline}</CopyText.Overline>
          <CopyText.Heading variant="h2" tag="h3">
            {copy.heading}
          </CopyText.Heading>

          {copy.list.map(({ heading, description }) => (
            <FeatureBoxBase key={heading} headerPosition="top" alignment="left">
              <FeatureBox.Body>
                <FeatureBox.Title>{heading}</FeatureBox.Title>
                <FeatureBox.Text>{description}</FeatureBox.Text>
              </FeatureBox.Body>
            </FeatureBoxBase>
          ))}

          <CopyText.Link href={getAbsoluteUrl(copy.buttonUrl, pageConfig)}>{copy.buttonText}</CopyText.Link>
        </CopyTextBase>
      </SectionCopy>

      <Section.Contents>
        <picture>
          <source type="image/webp" srcSet={`${AIReadyDataWebp}, ${AIReadyDataWebp2x} 2x`} />
          <StyledImage src={AIReadyDataPng2x} alt="AI ready data" width="652" height="508" loading="lazy" />
        </picture>
      </Section.Contents>
    </SectionBase>
  )
}

const SectionCopy = styled(Section.Copy)`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 440px;
    justify-self: end;
    align-self: center;
  }
`

const StyledImage = styled(Image)`
  filter: drop-shadow(${({ theme }) => theme.shadows[2]});
`

export default ReadyData
